/* CUSTOM: Product page controllers — rerender dispatcher, gallery wheel navigation, badges over the
   gallery, color picker position, gallery counter, USP tooltips, size gate, sticky add-to-cart bar with size sheet, single-open accordions.
   Loaded on product pages only from snippets/custom-global-head-assets.liquid; runs after theme.js. */
(() => {
  // --- CUSTOM SCRIPT START: Product update dispatcher ---
  // Fires `custom:product-updated` on document once per frame after `variant:change` (bubbles),
  // `product:rerender` (does not bubble; the capture listener fires BEFORE the theme swaps the DOM)
  // or `shopify:section:load`. Consumers therefore always see the swapped markup and run once.
  let productUpdateFrame = 0;

  const dispatchProductUpdated = (event) => {
    if (productUpdateFrame) return;

    productUpdateFrame = requestAnimationFrame(() => {
      productUpdateFrame = 0;
      document.dispatchEvent(new CustomEvent("custom:product-updated", { detail: { source: event.type } }));
    });
  };

  document.addEventListener("variant:change", dispatchProductUpdated);
  document.addEventListener("product:rerender", dispatchProductUpdated, true);
  document.addEventListener("shopify:section:load", dispatchProductUpdated);
  // --- CUSTOM SCRIPT END: Product update dispatcher ---

  // --- CUSTOM SCRIPT START: Product gallery wheel navigation (desktop) ---
  // One slide per horizontal wheel impulse on the PDP gallery (>= 1000px); vertical input is left to the page.
  // Delegated on document so it survives the rerender on variant change; registered only when a gallery exists.
  const PRODUCT_GALLERY_WHEEL_SELECTOR = ".product > product-gallery .product-gallery__carousel";
  const PRODUCT_GALLERY_WHEEL_MIN_DELTA = 40; // accumulated deltaX needed to advance
  const PRODUCT_GALLERY_WHEEL_COOLDOWN = 400; // ms, blocks trackpad momentum
  const PRODUCT_GALLERY_WHEEL_RESET = 200; // ms without input resets the accumulator
  const productGalleryDesktop = window.matchMedia("(min-width: 1000px)");
  const productGalleryWheelState = new WeakMap();

  function onProductGalleryWheel(event) {
    if (!productGalleryDesktop.matches) {
      return;
    }

    const carousel = event.target.closest?.(PRODUCT_GALLERY_WHEEL_SELECTOR);

    if (!carousel || typeof carousel.next !== "function") {
      return;
    }

    // Vertical intent: let the page scroll instead of hijacking it.
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
      return;
    }

    event.preventDefault();

    const now = performance.now();
    const state = productGalleryWheelState.get(carousel) || { delta: 0, lockedUntil: 0, timeout: 0 };

    productGalleryWheelState.set(carousel, state);

    if (now < state.lockedUntil) {
      return;
    }

    state.delta += event.deltaX;
    clearTimeout(state.timeout);
    state.timeout = setTimeout(() => {
      state.delta = 0;
    }, PRODUCT_GALLERY_WHEEL_RESET);

    if (Math.abs(state.delta) < PRODUCT_GALLERY_WHEEL_MIN_DELTA) {
      return;
    }

    if (state.delta > 0) {
      carousel.next();
    } else {
      carousel.previous();
    }

    state.delta = 0;
    state.lockedUntil = now + PRODUCT_GALLERY_WHEEL_COOLDOWN;
  }

  if (document.querySelector(PRODUCT_GALLERY_WHEEL_SELECTOR)) {
    document.addEventListener("wheel", onProductGalleryWheel, { passive: false });
  }
  // --- CUSTOM SCRIPT END: Product gallery wheel navigation (desktop) ---

  // --- CUSTOM SCRIPT START: Badges over the media gallery ---
  // Moves the theme's badges block into the gallery, where custom-product.css pins it to the top left of the
  // carousel. The block stays on the partial-rerender list, so the theme replaces it where it now sits.
  const GALLERY_BADGES_SELECTOR = '.product-info__block-item[data-block-type="badges"]';
  const GALLERY_BADGES_TARGET_SELECTOR = ".product-gallery__image-list";

  const placeGalleryBadges = () => {
    const item = document.querySelector(GALLERY_BADGES_SELECTOR);
    const target = item?.closest(".product")?.querySelector(GALLERY_BADGES_TARGET_SELECTOR);

    // No badges block, no gallery on this product, or already in place.
    if (!target || item.parentElement === target) return;

    target.prepend(item);
  };

  placeGalleryBadges();
  document.addEventListener("custom:product-updated", placeGalleryBadges);
  // --- CUSTOM SCRIPT END: Badges over the media gallery ---

  // --- CUSTOM SCRIPT START: Color picker position in the product info column ---
  // Moves the stand-alone color row (custom liquid block, first in the column) above the variant-picker
  // block at >= 1000px and back to the top below. DOM move instead of CSS `order`, so the theme's collapsing
  // block margins and the reading order stay intact; only touched when the position is wrong (keeps focus).
  const COLOR_PICKER_SELECTOR = ".custom-color-picker";
  const COLOR_PICKER_LIST_SELECTOR = ".product-info__block-list";
  const COLOR_PICKER_SIZE_BLOCK_SELECTOR =
    '.product-info__block-item[data-block-type="variant-picker"]';
  const colorPickerTwoColumnLayout = window.matchMedia("(min-width: 1000px)");

  const placeColorPicker = () => {
    const item = document
      .querySelector(COLOR_PICKER_SELECTOR)
      ?.closest(".product-info__block-item");
    const list = item?.closest(COLOR_PICKER_LIST_SELECTOR);

    // No color row on this product (sets render nothing) or not a product page.
    if (!list) return;

    if (colorPickerTwoColumnLayout.matches) {
      const sizeBlock = list.querySelector(COLOR_PICKER_SIZE_BLOCK_SELECTOR);

      // Without a variant picker (single-size styles) there is nothing to sit above.
      if (sizeBlock && sizeBlock.previousElementSibling !== item) {
        sizeBlock.before(item);
      }
    } else if (list.firstElementChild !== item) {
      // Back below the gallery: the block is configured as the first block of the column.
      list.prepend(item);
    }
  };

  placeColorPicker();
  colorPickerTwoColumnLayout.addEventListener("change", placeColorPicker);
  document.addEventListener("custom:product-updated", placeColorPicker);
  // --- CUSTOM SCRIPT END: Color picker position in the product info column ---

  // --- CUSTOM SCRIPT START: Gallery counter ---
  // Replaces the gallery dots (hidden in custom-product.css) with "1/8": below 1000px at the end of the color-name
  // row of the color picker, from 1000px left-aligned below the carousel. One element, moved by breakpoint; the
  // color row is re-rendered with its liquid block, so placement runs again on every product update. Without a
  // color row (sets) the counter stays below the carousel.
  const GALLERY_COUNTER_CAROUSEL_SELECTOR = ".product > product-gallery .product-gallery__carousel";
  const GALLERY_COUNTER_ROW_SELECTOR = ".custom-color-picker__header";
  const galleryCounterCarousels = new WeakSet();
  let galleryCounter = null;

  const updateGalleryCounter = () => {
    const carousel = document.querySelector(GALLERY_COUNTER_CAROUSEL_SELECTOR);
    if (!carousel || !galleryCounter) return;

    // cells skips media hidden by the variant filter, like the dots did.
    const total = carousel.cells?.length || 0;
    galleryCounter.hidden = total < 2;
    galleryCounter.textContent = `${Math.min((carousel.selectedIndex || 0) + 1, total)}/${total}`;
  };

  const placeGalleryCounter = () => {
    const carousel = document.querySelector(GALLERY_COUNTER_CAROUSEL_SELECTOR);
    if (!carousel) return;

    if (!galleryCounter) {
      galleryCounter = document.createElement("p");
      galleryCounter.className = "custom-gallery-counter";
    }

    // A full rerender brings a new carousel element.
    if (!galleryCounterCarousels.has(carousel)) {
      galleryCounterCarousels.add(carousel);
      carousel.addEventListener("carousel:change", updateGalleryCounter);
    }

    const row = productGalleryDesktop.matches ? null : document.querySelector(GALLERY_COUNTER_ROW_SELECTOR);

    if (row) {
      if (galleryCounter.parentElement !== row) row.append(galleryCounter);
    } else if (carousel.nextElementSibling !== galleryCounter) {
      carousel.after(galleryCounter);
    }

    updateGalleryCounter();
  };

  placeGalleryCounter();
  productGalleryDesktop.addEventListener("change", placeGalleryCounter);
  document.addEventListener("custom:product-updated", placeGalleryCounter);
  // --- CUSTOM SCRIPT END: Gallery counter ---

  // --- CUSTOM SCRIPT START: Favorites heart in the title row ---
  // Moves the Klaviyo favorites app block into the title block, where custom-product.css lays out title and
  // heart as one row. DOM move instead of absolute positioning: the static position would depend on where the
  // merchant happens to place the block in the editor.
  const FAVORITES_ITEM_SELECTOR =
    '.product-info__block-item:is([data-block-id*="customer_hub_wishlist"], :has(button[class*="kl-hub-favorites"])):not(:has(.custom-product-title))';
  const FAVORITES_TITLE_SELECTOR =
    ".product-info__block-item:has(.custom-product-title)";

  const placeFavorites = () => {
    const item = document.querySelector(FAVORITES_ITEM_SELECTOR);
    const titleItem = document.querySelector(FAVORITES_TITLE_SELECTOR);

    // No favorites block, no custom title block, or already in place.
    if (!item || !titleItem || item.parentElement === titleItem) return;

    titleItem.append(item);
  };

  placeFavorites();
  document.addEventListener("custom:product-updated", placeFavorites);
  // --- CUSTOM SCRIPT END: Favorites heart in the title row ---

  // --- CUSTOM SCRIPT START: USP subtitle tooltips on tap ---
  // Tapping a USP pill toggles its theme tooltip (the theme shows it on hover only); one open at a time,
  // a tap elsewhere or Escape closes it. Delegated, so re-rendered product markup stays covered.
  const USP_TRIGGER_SELECTOR = ".custom-product-usps__trigger";
  const USP_OPEN_ATTRIBUTE = "data-custom-tooltip-open";

  const closeUspTooltips = (except) => {
    document.querySelectorAll(`${USP_TRIGGER_SELECTOR}[${USP_OPEN_ATTRIBUTE}]`).forEach((trigger) => {
      if (trigger !== except) trigger.removeAttribute(USP_OPEN_ATTRIBUTE);
    });
  };

  document.addEventListener("click", (event) => {
    const origin = event.target instanceof Element ? event.target : null;
    const trigger = origin && origin.closest(USP_TRIGGER_SELECTOR);

    closeUspTooltips(trigger);
    trigger?.toggleAttribute(USP_OPEN_ATTRIBUTE);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeUspTooltips();
  });
  // --- CUSTOM SCRIPT END: USP subtitle tooltips on tap ---

  // --- CUSTOM SCRIPT START: Size gate before add to cart ---
  // Add to cart stays "Select your size" and opens the size sheet while her size is unknown: no own pick (trusted
  // change, finder result button, remembered pick of the type) and no available size-finder recommendation.
  // Own picks are remembered per product type for the session; a recommendation never replaces one.
  const SIZE_GATE_ROOT_SELECTOR = ".custom-sticky-size[data-custom-size-gate]";
  const SIZE_GATE_STATE_ATTRIBUTE = "data-custom-size-state";
  const SIZE_GATE_MEMORY_KEY = "custom-size-by-type";
  // formId -> Map(option position -> radio value the customer picked)
  const chosenSizes = new Map();

  const sizeGateRoot = (formId) =>
    document.querySelector(`${SIZE_GATE_ROOT_SELECTOR}[data-form-id="${CSS.escape(formId)}"]`);

  const sizeGateSheet = (formId) => document.getElementById(`custom-sticky-size-popover-${formId}`);

  // Size positions that need a choice (more than one value); read from the root or the open sheet at <body>.
  const sizeGatePositions = (formId) => {
    const positions = new Set();
    [sizeGateRoot(formId), sizeGateSheet(formId)].forEach((scope) => {
      scope?.querySelectorAll("[data-custom-size-position]").forEach((group) => {
        positions.add(group.getAttribute("data-custom-size-position"));
      });
    });
    return positions;
  };

  const selectedSizeValue = (formId, position) => {
    const form = document.forms[formId];
    if (!form) return null;
    const checked = Array.from(form.elements).find(
      (element) => element.matches?.(`input[data-option-position="${position}"]:checked`)
    );
    return checked ? checked.value : null;
  };

  // Chosen = every size position still shows the value the customer picked; a later preselect undoes it.
  const isSizeChosen = (formId) => {
    const chosen = chosenSizes.get(formId);
    const positions = sizeGatePositions(formId);
    if (!positions.size) return true;
    if (!chosen) return false;
    return Array.from(positions).every((position) => chosen.get(position) === selectedSizeValue(formId, position));
  };

  // Size groups of the form in option order; the open sheet sits at <body>, otherwise inside the root.
  const sizeGroups = (formId) => {
    const root = sizeGateRoot(formId);
    const scope = root?.querySelector(".custom-sticky-size__sheet") ? root : sizeGateSheet(formId);
    return Array.from(scope?.querySelectorAll("[data-custom-size-position]") || []);
  };

  const rowLabel = (row) => row?.querySelector(".custom-sticky-size__value")?.textContent.trim() || "";

  // Session memory: product type -> label of the picked size per size group (value ids differ per product).
  const readSizeMemory = () => {
    try {
      return JSON.parse(sessionStorage.getItem(SIZE_GATE_MEMORY_KEY)) || {};
    } catch (error) {
      return {};
    }
  };

  const writeSizeMemory = (memory) => {
    try {
      sessionStorage.setItem(SIZE_GATE_MEMORY_KEY, JSON.stringify(memory));
    } catch (error) {
      /* no memory, no problem */
    }
  };

  const storeChosenSizes = (formId) => {
    const type = sizeGateRoot(formId)?.getAttribute("data-custom-size-type");
    const chosen = chosenSizes.get(formId);
    if (!type || !chosen) return;

    const memory = readSizeMemory();
    memory[type] = sizeGroups(formId).map((group) => {
      const value = chosen.get(group.getAttribute("data-custom-size-position"));
      const input = value && group.querySelector(`input[value="${CSS.escape(value)}"]`);
      return input ? rowLabel(input.closest(".custom-sticky-size__option")) : "";
    });
    writeSizeMemory(memory);
  };

  // "formId|position" of own picks; a size-finder recommendation only fills positions without one.
  const ownSizePicks = new Set();

  const rememberChosenSize = (formId, position, value) => {
    if (!chosenSizes.has(formId)) chosenSizes.set(formId, new Map());
    chosenSizes.get(formId).set(position, value);
    ownSizePicks.add(`${formId}|${position}`);
    storeChosenSizes(formId);
  };

  // The theme's own (hidden) picker radio for a value — not the sheet copy.
  const pickerRadioFor = (formId, position, value) =>
    Array.from(
      document.querySelectorAll(
        `input[data-option-position="${position}"][value="${CSS.escape(value)}"][form="${CSS.escape(formId)}"]`
      )
    ).find((input) => !input.id.startsWith("custom-sticky-size-"));

  // Remembered size of this product type: selected (sold out too — the bar then offers "Notify me") and counted as
  // picked; a size this product does not carry leaves the form unchosen. Size groups (sets) go one rerender at a time.
  const restoringSizes = new Map(); // formId -> remaining picks to select
  const restoredForms = new Set();

  const selectNextRestoredSize = (formId) => {
    const picks = restoringSizes.get(formId);
    if (!picks) return;
    const pending = picks.find((pick) => selectedSizeValue(formId, pick.position) !== pick.value);
    if (!pending) {
      restoringSizes.delete(formId);
      return;
    }
    picks.splice(picks.indexOf(pending), 1);
    pickerRadioFor(formId, pending.position, pending.value)?.click();
  };

  const restoreRememberedSize = (formId) => {
    if (restoredForms.has(formId)) return;
    restoredForms.add(formId);

    const type = sizeGateRoot(formId)?.getAttribute("data-custom-size-type");
    const labels = type ? readSizeMemory()[type] : null;
    if (!Array.isArray(labels)) return;

    const picks = sizeGroups(formId).map((group, index) => {
      const row = Array.from(group.querySelectorAll(".custom-sticky-size__option")).find(
        (option) => labels[index] && rowLabel(option) === labels[index]
      );
      const input = row?.querySelector("input");
      if (!input || input.disabled) return null;
      const value = input.value;
      return value ? { position: group.getAttribute("data-custom-size-position"), value } : null;
    });
    if (!picks.length || picks.some((pick) => !pick)) return;

    picks.forEach((pick) => rememberChosenSize(formId, pick.position, pick.value));
    restoringSizes.set(formId, picks.slice());
    selectNextRestoredSize(formId);
  };

  const restoreAllRememberedSizes = () => {
    document.querySelectorAll(SIZE_GATE_ROOT_SELECTOR).forEach((root) => {
      const formId = root.getAttribute("data-form-id");
      if (formId) restoreRememberedSize(formId);
    });
  };

  // Wraps the theme's button text once, so CSS can swap it for the gate label without touching the node.
  const labelGateButton = (buyButtons, gateLabel) => {
    const button = buyButtons.querySelector(':scope > button[type="submit"]');
    if (!button || button.querySelector(".custom-size-gate__label")) return;

    const original = document.createElement("span");
    original.className = "custom-size-gate__default";
    original.append(...button.childNodes);

    const label = document.createElement("span");
    label.className = "custom-size-gate__label";
    label.textContent = gateLabel;

    button.append(original, label);
  };

  const applySizeGate = () => {
    document.querySelectorAll(SIZE_GATE_ROOT_SELECTOR).forEach((root) => {
      const formId = root.getAttribute("data-form-id");
      if (!formId) return;

      const state = isSizeChosen(formId) ? "set" : "unset";
      root.setAttribute(SIZE_GATE_STATE_ATTRIBUTE, state);
      (root.querySelector(".custom-sticky-size__sheet") || sizeGateSheet(formId))?.setAttribute(SIZE_GATE_STATE_ATTRIBUTE, state);

      document.querySelectorAll(`buy-buttons[form="${CSS.escape(formId)}"]`).forEach((buyButtons) => {
        labelGateButton(buyButtons, root.getAttribute("data-custom-size-gate") || "");
        buyButtons.setAttribute(SIZE_GATE_STATE_ATTRIBUTE, state);
      });
    });
  };

  if (document.querySelector(SIZE_GATE_ROOT_SELECTOR)) {
    restoreAllRememberedSizes();
    applySizeGate();
  }
  document.addEventListener("custom:product-updated", () => {
    restoringSizes.forEach((picks, formId) => selectNextRestoredSize(formId));
    applySizeGate();
  });

  // A new quiz result replaces the remembered sizes and own picks (the finder preselects its recommendation instead).
  window.addEventListener("mm:size-profile-updated", () => {
    writeSizeMemory({});
    ownSizePicks.clear();
  });

  // Recommendation of a completed quiz (custom-product-size-finder.js): counts as picked where the customer has not
  // chosen herself; stays out of the per-type memory. The finder's preselect then makes it the shown size.
  document.addEventListener("custom:size-recommended", (event) => {
    const { formId, position, value } = event.detail || {};
    if (!formId || !position || !value || ownSizePicks.has(`${formId}|${position}`)) return;
    if (!sizeGatePositions(formId).has(position)) return;

    if (!chosenSizes.has(formId)) chosenSizes.set(formId, new Map());
    if (chosenSizes.get(formId).get(position) === value) return;
    chosenSizes.get(formId).set(position, value);
    applySizeGate();
  });

  // A genuine size pick; the programmatic preselect of the size finder is untrusted and never counts.
  document.addEventListener("change", (event) => {
    const input = event.target;
    if (!event.isTrusted || !(input instanceof HTMLInputElement)) return;

    const formId = input.getAttribute("form") || input.form?.getAttribute("id");
    const position = input.dataset.optionPosition;
    if (!formId || !position || !sizeGatePositions(formId).has(position)) return;

    rememberChosenSize(formId, position, input.value);
    applySizeGate();
  }, true);

  // Size accepted in the size finder ("Select size M", custom-product-size-finder.js): counts as the customer's
  // pick. The radio is selected after the finder modal has closed — the sheet radio while the sheet is open.
  let pendingAcceptedSize = null;

  const selectAcceptedSize = ({ formId, position, value }) => {
    const sheet = sizeGateSheet(formId);
    const selector = `input[data-option-position="${position}"][value="${CSS.escape(value)}"]`;
    const radio = sheet?.open ? sheet.querySelector(selector) : pickerRadioFor(formId, position, value);

    if (radio && selectedSizeValue(formId, position) !== value) {
      radio.click();
    } else {
      applySizeGate();
      if (sheet?.open) sheet.hide();
    }
  };

  document.addEventListener("custom:size-accept", (event) => {
    const { formId, position, value } = event.detail || {};
    if (!formId || !position || !value) return;
    rememberChosenSize(formId, position, value);
    pendingAcceptedSize = { formId, position, value };
  });

  document.addEventListener("dialog:after-hide", (event) => {
    if (!pendingAcceptedSize || !(event.target instanceof Element)) return;
    if (!event.target.matches('x-modal[id^="size-chart-"]')) return;

    const accepted = pendingAcceptedSize;
    pendingAcceptedSize = null;
    requestAnimationFrame(() => selectAcceptedSize(accepted));
  }, true);

  // Switch to the recommendation from the line above the size field (custom-product-size-finder.js): an own pick,
  // selected right away — no overlay is open, the line sits outside the sheet.
  document.addEventListener("custom:size-pick", (event) => {
    const { formId, position, value } = event.detail || {};
    if (!formId || !position || !value) return;
    rememberChosenSize(formId, position, value);

    const radio = pickerRadioFor(formId, position, value);
    if (radio && selectedSizeValue(formId, position) !== value) {
      radio.click();
    } else {
      applySizeGate();
    }
  });

  // Unchosen size: the submit opens the sheet instead of adding to cart. Capture at document runs before the
  // theme's submit listener on the form.
  document.addEventListener("submit", (event) => {
    const form = event.target;
    const formId = form instanceof HTMLFormElement ? form.getAttribute("id") : null;
    if (!formId || !sizeGateRoot(formId) || isSizeChosen(formId)) return;

    event.preventDefault();
    event.stopPropagation();
    sizeGateSheet(formId)?.show?.();
  }, true);

  // Quick buy content is rebuilt on every open: start unchosen, apply the remembered size, label the buttons.
  document.addEventListener("dialog:before-show", (event) => {
    const modal = event.target;
    if (!(modal instanceof Element) || !modal.matches("quick-buy-modal")) return;

    [chosenSizes, restoringSizes, restoredForms].forEach((store) => {
      Array.from(store.keys())
        .filter((formId) => formId.startsWith("product-form-quick-buy-"))
        .forEach((formId) => store.delete(formId));
    });
    Array.from(ownSizePicks)
      .filter((key) => key.startsWith("product-form-quick-buy-"))
      .forEach((key) => ownSizePicks.delete(key));
    requestAnimationFrame(() => {
      restoreAllRememberedSizes();
      applySizeGate();
    });
  }, true);

  document.addEventListener("dialog:after-show", (event) => {
    if (event.target instanceof Element && event.target.matches("quick-buy-modal")) applySizeGate();
  }, true);
  // --- CUSTOM SCRIPT END: Size gate before add to cart ---

  // --- CUSTOM SCRIPT START: Sticky add-to-cart bar ---
  // Moves the size selector out of the variant-picker block into the buy-buttons block on every width (below
  // 700px that block is pinned as the bar once it has scrolled out at the top) and publishes the bar height as
  // `--custom-sticky-atc-height`.
  // Sync runs on `custom:product-updated`, one frame after the DOM swap.
  const STICKY_ATC_MARKER_SELECTOR = ".custom-sticky-atc-enable";
  const STICKY_ATC_BAR_SELECTOR = '.product-info__block-item[data-block-type="buy-buttons"]';
  const STICKY_ATC_HEIGHT_VARIABLE = "--custom-sticky-atc-height";
  const STICKY_SIZE_SELECTOR = ".custom-sticky-size";
  // :not(quick-buy-modal *), because the popup renders the same block — only the main product's belongs in the bar.
  const STICKY_SIZE_BLOCK_SELECTOR =
    '.product-info__block-item[data-block-type="variant-picker"]:not(quick-buy-modal *)';
  // A selector that still sits where the server rendered it (inside the variant-picker block) is fresh.
  const STICKY_SIZE_FRESH_SELECTOR = `${STICKY_SIZE_BLOCK_SELECTOR} ${STICKY_SIZE_SELECTOR}`;
  const STICKY_SIZE_SHEET_SELECTOR = "x-popover.custom-sticky-size__sheet";
  const STICKY_SIZE_TRIGGER_SELECTOR = ".custom-sticky-size__trigger";
  const stickySizeInBar = window.matchMedia("(max-width: 699px)");

  // Marker absent (no custom liquid block) → feature is off, and nothing below needs to run.
  if (document.querySelector(STICKY_ATC_MARKER_SELECTOR)) {
    let observedStickyAtcBar = null;

    const publishStickyAtcHeight = () => {
      const bar = document.querySelector(STICKY_ATC_BAR_SELECTOR);

      // 0px above the mobile breakpoint, where the block sits in the column and needs no reservation.
      document.documentElement.style.setProperty(
        STICKY_ATC_HEIGHT_VARIABLE,
        bar ? `${bar.offsetHeight}px` : "0px"
      );
    };

    const stickyAtcResizeObserver = new ResizeObserver(publishStickyAtcHeight);

    // An open sheet was moved to <body> by the theme: hide() lets the theme re-append it into its wrapper,
    // even a detached one. Never remove the sheet element by hand.
    const closeStickySizeSheet = (element) => {
      const sheet = element?.querySelector(STICKY_SIZE_SHEET_SELECTOR);
      if (sheet && sheet.open) sheet.hide();
    };

    const placeStickySize = (bar) => {
      const fresh = document.querySelector(STICKY_SIZE_FRESH_SELECTOR);
      const mounted = bar
        ? Array.from(bar.children).find((child) => child.matches(STICKY_SIZE_SELECTOR))
        : null;

      if (!fresh || !bar) return;

      const hadFocus = !!(mounted && mounted.contains(document.activeElement));

      if (mounted) {
        closeStickySizeSheet(mounted);
        mounted.remove();
      }

      // Hide footer buttons whose target dialog is not on this page (wrapper incl. icon, not just the button).
      fresh.querySelectorAll("[data-custom-sticky-open]").forEach((button) => {
        const wrapper = button.closest(".custom-sticky-size__fit") || button;
        wrapper.hidden = !document.getElementById(button.getAttribute("data-custom-sticky-open") || "");
      });

      const productForm = bar.querySelector("product-form");
      if (productForm) {
        bar.insertBefore(fresh, productForm);
      } else {
        bar.prepend(fresh);
      }

      if (hadFocus) fresh.querySelector(STICKY_SIZE_TRIGGER_SELECTOR)?.focus({ preventScroll: true });
    };

    const syncStickyAtcBar = () => {
      const bar = document.querySelector(STICKY_ATC_BAR_SELECTOR);

      placeStickySize(bar);

      if (bar !== observedStickyAtcBar) {
        if (observedStickyAtcBar) stickyAtcResizeObserver.unobserve(observedStickyAtcBar);
        observedStickyAtcBar = bar;
        if (bar) stickyAtcResizeObserver.observe(bar);
      }

      publishStickyAtcHeight();
    };

    syncStickyAtcBar();
    document.addEventListener("custom:product-updated", syncStickyAtcBar);
    stickySizeInBar.addEventListener("change", syncStickyAtcBar);

    // Below 700px the block stays in the column and is pinned as the bar only once it has scrolled out at the top
    // (data-custom-sticky-atc-pinned on the block list); scrolling back to its spot releases it. A placeholder of
    // the block's in-column height keeps the content from jumping while it is fixed.
    const STICKY_ATC_PINNED_ATTRIBUTE = "data-custom-sticky-atc-pinned";
    const STICKY_ATC_PLACEHOLDER_CLASS = "custom-sticky-atc-placeholder";
    let stickyAtcPinFrame = 0;

    const stickyAtcPlaceholder = (bar) => {
      let placeholder = bar.previousElementSibling;
      if (!placeholder?.classList.contains(STICKY_ATC_PLACEHOLDER_CLASS)) {
        placeholder = document.createElement("div");
        placeholder.className = STICKY_ATC_PLACEHOLDER_CLASS;
        placeholder.setAttribute("aria-hidden", "true");
        placeholder.hidden = true;
        bar.before(placeholder);
      }
      return placeholder;
    };

    const updateStickyAtcPin = () => {
      stickyAtcPinFrame = 0;
      const bar = document.querySelector(STICKY_ATC_BAR_SELECTOR);
      const list = bar?.closest(".product-info__block-list");
      if (!list) return;

      const placeholder = stickyAtcPlaceholder(bar);
      const pinned = list.hasAttribute(STICKY_ATC_PINNED_ATTRIBUTE);
      // Where the block sits in the column: itself while in flow, the placeholder while pinned.
      const spot = (pinned ? placeholder : bar).getBoundingClientRect();
      const shouldPin = stickySizeInBar.matches && spot.bottom < 0;

      if (shouldPin === pinned) return;

      if (shouldPin) {
        placeholder.style.setProperty("--custom-sticky-atc-placeholder-height", `${bar.offsetHeight}px`);
        placeholder.hidden = false;
        list.setAttribute(STICKY_ATC_PINNED_ATTRIBUTE, "");
      } else {
        list.removeAttribute(STICKY_ATC_PINNED_ATTRIBUTE);
        placeholder.hidden = true;
      }
    };

    const requestStickyAtcPin = () => {
      if (!stickyAtcPinFrame) stickyAtcPinFrame = requestAnimationFrame(updateStickyAtcPin);
    };

    updateStickyAtcPin();
    window.addEventListener("scroll", requestStickyAtcPin, { passive: true });
    window.addEventListener("resize", requestStickyAtcPin);
    stickySizeInBar.addEventListener("change", requestStickyAtcPin);
    document.addEventListener("custom:product-updated", requestStickyAtcPin);

    // Open the fit-note modal ON TOP of the open sheet via show() — a synthetic click outside the sheet
    // would close it (focus traps stack, see Themes/MEMORY.md).
    document.addEventListener("click", (event) => {
      const origin = event.target instanceof Element ? event.target : null;
      const button = origin && origin.closest("[data-custom-sticky-open]");
      if (!button) return;

      const target = document.getElementById(button.getAttribute("data-custom-sticky-open") || "");
      if (!target || typeof target.show !== "function") return;

      target.show();
    });

    // After the sheet closes, focus the current trigger — or add to cart while the size gate hides the trigger.
    // `dialog:after-hide` does not bubble → capture phase; one frame lets the trap finish first.
    document.addEventListener("dialog:after-hide", (event) => {
      const sheet = event.target;
      if (!(sheet instanceof Element) || !sheet.matches(STICKY_SIZE_SHEET_SELECTOR)) return;

      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active && active !== document.body && active.isConnected) return;

        const bar = document.querySelector(STICKY_ATC_BAR_SELECTOR);
        const target = [
          bar?.querySelector(STICKY_SIZE_TRIGGER_SELECTOR),
          bar?.querySelector('buy-buttons > button[type="submit"]'),
        ].find((element) => element && element.getClientRects().length);
        target?.focus({ preventScroll: true });
      });
    }, true);
  }
  // --- CUSTOM SCRIPT END: Sticky add-to-cart bar ---

  // --- CUSTOM SCRIPT START: Single-open accordions in the product info column ---
  // Opening one product accordion closes its open siblings. Trigger is the `aria-expanded` state (covers
  // pointer, keyboard and the theme editor); closing runs through the element's own close(). Scope = every
  // container the theme renders product accordions into; footer and FAQ accordions are unaffected.
  const PRODUCT_ACCORDION_SCOPE_SELECTOR =
    ".accordion-group, .product-content-below-gallery, .product-info__block-list";

  const closeSiblingAccordions = (details) => {
    const current = details.closest("accordion-disclosure");
    const scope = current && current.closest(PRODUCT_ACCORDION_SCOPE_SELECTOR);

    if (!scope) return;

    scope.querySelectorAll("accordion-disclosure").forEach((other) => {
      if (other === current || typeof other.close !== "function") return;
      if (other.disclosureElement?.getAttribute("aria-expanded") === "true") other.close();
    });
  };

  // Observed on body so re-rendered product markup stays covered.
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      const details = mutation.target;

      if (
        details instanceof Element &&
        details.matches("details[aria-expanded='true']")
      ) {
        closeSiblingAccordions(details);
      }
    });
  }).observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ["aria-expanded"],
  });
  // --- CUSTOM SCRIPT END: Single-open accordions in the product info column ---
})();
